import { 
  Box, 
  Grid, 
  Typography, 
  Card, 
  CardContent, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip as MuiTooltip
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, limit, where } from "firebase/firestore";
import { DATABASE } from "../../../Config/firebase";
import { getYearlyGraphSummary } from "../../../services/yearlyGraphService";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as CheckCircleIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon
} from "@mui/icons-material";
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  Filler
);

const FundAnalytics = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [yearlyGraphData, setYearlyGraphData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedYear, setSelectedYear] = useState('All Years');
  const [selectedMonth, setSelectedMonth] = useState('All Months');
  const [selectedRating, setSelectedRating] = useState('All Ratings');
  const [stats, setStats] = useState({
    totalMonths: 0,
    positiveMonths: 0,
    winRate: 0,
    bestMonth: 0,
    averageReturn: 0,
    volatility: 0,
    totalReturn: 0,
    past12MonthsReturn: 0,
    worstMonth: 0
  });

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  useEffect(() => {
    loadYearlyGraphData();
  }, []);

  useEffect(() => {
    if (yearlyGraphData.length > 0) {
      console.log("Yearly graph data loaded:", yearlyGraphData.length, "records");
      console.log("Sample record structure:", JSON.stringify(yearlyGraphData[0], null, 2));
      applyFilters();
      calculateStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearlyGraphData, selectedYear, selectedMonth, selectedRating]);


  const loadYearlyGraphData = async () => {
    setIsLoading(true);
    try {
      // Get data from consolidated document (or fallback to collection)
      const summary = await getYearlyGraphSummary();
      
      if (summary.source === "collection") {
        console.warn("⚠️ Consolidated document not found. Consider running initial migration.");
      }
      
      let data = summary.months || [];
      
      // Filter out invalid records (missing year or month)
      const validData = data.filter(item => item.year && item.month && !isNaN(item.year) && !isNaN(item.month));
      
      // Filter out future months (e.g., October 2025 if we're still in September)
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      
      const filteredByDate = validData.filter(item => {
        if (item.year > currentYear) return false;
        if (item.year === currentYear && item.month > currentMonth) return false;
        return true;
      });
      
      // Get last 12 months only for this view
      const last12Months = filteredByDate.slice(-12);
      
      console.log("Loaded yearly graph data:", filteredByDate.length, "months");
      console.log("Last 12 months:", last12Months.length, "months");
      console.log("Source:", summary.source);
      console.log("Date range:", last12Months.length > 0 ? `${last12Months[0].year}-${last12Months[0].month} to ${last12Months[last12Months.length-1].year}-${last12Months[last12Months.length-1].month}` : "No data");
      
      setYearlyGraphData(filteredByDate); // Keep all data for stats calculation
      setFilteredData(last12Months); // Use only last 12 months for chart display
    } catch (error) {
      console.error("❌ Error loading yearly graph data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...yearlyGraphData];
    
    if (selectedYear !== 'All Years') {
      filtered = filtered.filter(item => item.year === parseInt(selectedYear));
    }
    
    if (selectedMonth !== 'All Months') {
      filtered = filtered.filter(item => item.month === months.indexOf(selectedMonth) + 1);
    }
    
    if (selectedRating !== 'All Ratings') {
      filtered = filtered.filter(item => {
        // Use ONLY amount field (same as Flutter app)
        const performance = item.amount || 0;
        switch(selectedRating) {
          case 'Exceptional': return performance > 5;
          case 'Excellent': return performance >= 4 && performance <= 5;
          case 'Good': return performance >= 2 && performance < 4;
          case 'Moderate': return performance >= 0 && performance < 2;
          case 'Negative': return performance < 0;
          default: return true;
        }
      });
    }
    
    setFilteredData(filtered);
  };

  const calculateStats = () => {
    if (yearlyGraphData.length === 0) {
      console.log("No data available for stats calculation");
      return;
    }
    
    // Data is already sorted chronologically (oldest to newest)
    const sortedData = [...yearlyGraphData];
    
    console.log("Calculating stats from", sortedData.length, "records");
    console.log("Sample record:", sortedData[0]);
    
    const returns = [];
    let cumulativeTotal = 0;
    
    sortedData.forEach(item => {
      // Use ONLY amount field (same as Flutter app)
      const performance = item.amount || 0;
      console.log("Performance value:", performance, "from item:", item);
      
      if (!isNaN(performance) && performance !== null && performance !== undefined) {
        cumulativeTotal += parseFloat(performance);
        returns.push(parseFloat(performance));
      }
    });
    
    console.log("Total returns:", returns.length);
    
    const positiveMonths = sortedData.filter(item => {
      // Use ONLY amount field (same as Flutter app)
      const p = item.amount || 0;
      return !isNaN(p) && p > 0;
    }).length;
    
    const winRate = sortedData.length > 0 ? (positiveMonths / sortedData.length) * 100 : 0;
    
    const validReturns = returns.filter(r => !isNaN(r) && r !== null && r !== undefined);
    const bestMonth = validReturns.length > 0 ? Math.max(...validReturns) : 0;
    const worstMonth = validReturns.length > 0 ? Math.min(...validReturns) : 0;
    
    const avgReturn = returns.length > 0 ? returns.reduce((a, b) => a + b, 0) / returns.length : 0;
    
    const variance = returns.length > 0 
      ? returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / returns.length 
      : 0;
    const volatility = Math.sqrt(variance);
    
    // Past 12 months (last 12 items from the sorted data)
    const past12Months = sortedData.slice(-12);
    const past12Return = past12Months.reduce((sum, item) => {
      // Use ONLY amount field (same as Flutter app)
      const p = item.amount || 0;
      return sum + (isNaN(p) ? 0 : parseFloat(p));
    }, 0);
    
    console.log("Calculated stats:", {
      totalMonths: sortedData.length,
      positiveMonths: positiveMonths,
      winRate: winRate,
      bestMonth: bestMonth,
      averageReturn: avgReturn,
      volatility: volatility,
      totalReturn: cumulativeTotal,
      past12MonthsReturn: past12Return,
      worstMonth: worstMonth
    });
    
    setStats({
      totalMonths: sortedData.length,
      positiveMonths: positiveMonths,
      winRate: winRate,
      bestMonth: bestMonth,
      averageReturn: avgReturn,
      volatility: volatility,
      totalReturn: cumulativeTotal,
      past12MonthsReturn: past12Return,
      worstMonth: worstMonth
    });
  };

  const getPerformanceCategory = (performance) => {
    if (performance > 5) return { label: 'Exceptional', color: '#ff9800' };
    if (performance >= 4 && performance <= 5) return { label: 'Excellent', color: '#ffc107' };
    if (performance >= 2 && performance < 4) return { label: 'Good', color: '#2196f3' };
    if (performance >= 0 && performance < 2) return { label: 'Moderate', color: '#9c27b0' };
    return { label: 'Negative', color: '#f44336' };
  };

  const getCumulativeReturn = (index) => {
    if (index < 0 || index >= filteredData.length) return 0;
    
    // START FROM 0 - don't use first month's point as base
    // Calculate cumulative from amount values within 12-month window (starting at 0)
    // This matches Flutter app exactly
    let cumulativeTotal = 0;
    for (let i = 0; i <= index && i < filteredData.length; i++) {
      const item = filteredData[i];
      // Use ONLY amount field (same as Flutter app)
      const monthlyPercentage = item?.amount || 0;
      // Amount is already in percentage format (e.g., 2.04 = 2.04%)
      cumulativeTotal += monthlyPercentage;
    }
    
    return cumulativeTotal;
  };

  // Prepare chart data
  const chartData = {
    labels: filteredData.map(item => `${months[item.month - 1]} ${item.year}`),
    datasets: [
      {
        type: 'bar',
        label: 'Monthly Performance (%)',
        data: filteredData.map(item => item.amount || 0),
        backgroundColor: filteredData.map(item => {
          // Use ONLY amount field (same as Flutter app)
          const p = item.amount || 0;
          const category = getPerformanceCategory(p);
          return category.color;
        }),
        borderColor: filteredData.map(item => {
          // Use ONLY amount field (same as Flutter app)
          const p = item.amount || 0;
          const category = getPerformanceCategory(p);
          return category.color;
        }),
        borderWidth: 1,
        yAxisID: 'y'
      },
      {
        type: 'line',
        label: 'Cumulative Trend (%)',
        data: filteredData.map((_, index) => getCumulativeReturn(index)),
        borderColor: '#00c820',
        backgroundColor: 'rgba(0, 200, 32, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        yAxisID: 'y1'
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        labels: {
          color: '#fff'
        },
        display: true
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            if (context.datasetIndex === 0) {
              const category = getPerformanceCategory(context.parsed.y);
              return `Monthly Performance: ${context.parsed.y.toFixed(2)}% (${category.label})`;
            } else {
              return `Total Fund Growth (Cumulative): ${context.parsed.y.toFixed(2)}%`;
            }
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: '#aaa'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Monthly Performance (%)',
          color: '#fff'
        },
        ticks: {
          color: '#aaa'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Cumulative Trend (%)',
          color: '#fff'
        },
        ticks: {
          color: '#aaa'
        },
        grid: {
          drawOnChartArea: false
        }
      }
    }
  };

  const getYearOptions = () => {
    const years = [...new Set(yearlyGraphData.map(item => item.year))].sort((a, b) => b - a);
    return years;
  };

  return (
    <Box sx={{ 
      width: "100%", 
      p: 3,
      background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
      minHeight: "100vh",
      paddingTop: { xs: '60px', sm: "0px" }
    }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: "700", color: "#fff", mb: 1 }}>
            Fund Performance History
          </Typography>
          <Typography variant="body1" sx={{ color: "#aaa" }}>
            Historical performance data with detailed analytics
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <MuiTooltip title="Refresh Data">
            <IconButton 
              onClick={() => loadYearlyGraphData()}
              disabled={isLoading}
              sx={{ 
                color: "#fff",
                backgroundColor: "rgba(42, 245, 152, 0.1)",
                "&:hover": { backgroundColor: "rgba(42, 245, 152, 0.2)" },
                "&:disabled": { opacity: 0.5 }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </MuiTooltip>
          <Button
            variant="outlined"
            sx={{
              color: "#fff",
              borderColor: "rgba(42, 245, 152, 0.3)",
              "&:hover": {
                borderColor: "#2af598",
                backgroundColor: "rgba(42, 245, 152, 0.1)"
              }
            }}
          >
            Advanced Report
          </Button>
        </Box>
      </Box>

      {/* Monthly Records Input Section */}
      <Card sx={{
        background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
        mb: 4
      }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: "600", color: "#fff", mb: 3 }}>
            Monthly Records
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Year"
                value="2025"
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.3)"
                    }
                  },
                  "& .MuiInputLabel-root": {
                    color: "#aaa"
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: "#aaa" }}>Month</InputLabel>
                <Select
                  value=""
                  label="Month"
                  sx={{
                    color: "#fff",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.3)"
                    }
                  }}
                >
                  {months.map((month, index) => (
                    <MenuItem key={index} value={month}>{month}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Performance (%)"
                value="0"
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.3)"
                    }
                  },
                  "& .MuiInputLabel-root": {
                    color: "#aaa"
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3} sx={{ display: "flex", alignItems: "center" }}>
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                fullWidth
                sx={{
                  background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                  "&:hover": {
                    background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                    opacity: 0.9
                  }
                }}
              >
                + Audit Record
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Performance KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
            border: "1px solid rgba(42, 245, 152, 0.3)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)"
          }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <TrendingUpIcon sx={{ color: "#2af598", fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: "#aaa" }}>Total Return Since Inception</Typography>
              </Box>
              <Typography variant="h4" sx={{ color: "#2af598", fontWeight: "700" }}>
                +{stats.totalReturn.toFixed(2)}%
              </Typography>
              <Typography variant="caption" sx={{ color: "#aaa" }}>
                {stats.totalMonths} months tracked
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
            border: "1px solid rgba(42, 245, 152, 0.3)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)"
          }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <TrendingUpIcon sx={{ color: "#2af598", fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: "#aaa" }}>Past 12 Months Return</Typography>
              </Box>
              <Typography variant="h4" sx={{ color: "#2af598", fontWeight: "700" }}>
                +{stats.past12MonthsReturn.toFixed(2)}%
              </Typography>
              <Typography variant="caption" sx={{ color: "#aaa" }}>
                Rolling 12 months
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
            border: "1px solid rgba(255, 193, 7, 0.3)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)"
          }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <TrendingUpIcon sx={{ color: "#ffc107", fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: "#aaa" }}>Best Month</Typography>
              </Box>
              <Typography variant="h4" sx={{ color: "#ffc107", fontWeight: "700" }}>
                +{stats.bestMonth.toFixed(2)}%
              </Typography>
              <Typography variant="caption" sx={{ color: "#aaa" }}>
                Historical Best
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
            border: "1px solid rgba(245, 42, 45, 0.3)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)"
          }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <TrendingDownIcon sx={{ color: "#f44336", fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: "#aaa" }}>Worst Month</Typography>
              </Box>
              <Typography variant="h4" sx={{ color: "#f44336", fontWeight: "700" }}>
                {stats.worstMonth.toFixed(2)}%
              </Typography>
              <Typography variant="caption" sx={{ color: "#aaa" }}>
                Historical Low
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Statistics and Recent Highlights */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{
            background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)"
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "600", color: "#fff", mb: 2 }}>
                Performance Statistics
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Total Months Tracked</Typography>
                  <Typography variant="h6" sx={{ color: "#fff", fontWeight: "700" }}>{stats.totalMonths}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Positive Months</Typography>
                  <Typography variant="h6" sx={{ color: "#2af598", fontWeight: "700" }}>{stats.positiveMonths}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Win Rate</Typography>
                  <Typography variant="h6" sx={{ color: "#2af598", fontWeight: "700" }}>{stats.winRate.toFixed(1)}%</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Best Month</Typography>
                  <Typography variant="h6" sx={{ color: "#ffc107", fontWeight: "700" }}>+{stats.bestMonth.toFixed(2)}%</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Average Return</Typography>
                  <Typography variant="h6" sx={{ color: "#2196f3", fontWeight: "700" }}>+{stats.averageReturn.toFixed(2)}%</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Volatility (StdDev)</Typography>
                  <Typography variant="h6" sx={{ color: "#9c27b0", fontWeight: "700" }}>{stats.volatility.toFixed(2)}%</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Total Return</Typography>
                  <Typography variant="h6" sx={{ color: "#2af598", fontWeight: "700" }}>+{stats.totalReturn.toFixed(2)}%</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Past 12 Months</Typography>
                  <Typography variant="h6" sx={{ color: "#2af598", fontWeight: "700" }}>+{stats.past12MonthsReturn.toFixed(2)}%</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Negative Months</Typography>
                  <Typography variant="h6" sx={{ color: "#f44336", fontWeight: "700" }}>{stats.totalMonths - stats.positiveMonths}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#aaa" }}>Worst Month</Typography>
                  <Typography variant="h6" sx={{ color: "#f44336", fontWeight: "700" }}>{stats.worstMonth.toFixed(2)}%</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{
            background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)"
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "600", color: "#fff", mb: 2 }}>
                Recent Performance Highlights
              </Typography>
              {[...yearlyGraphData].reverse().slice(0, 6).map((item, index) => {
                // Use ONLY amount field (same as Flutter app)
                const performance = item.amount || 0;
                const category = getPerformanceCategory(performance);
                return (
                  <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, pb: 1, borderBottom: index < 5 ? "1px solid rgba(255, 255, 255, 0.1)" : "none" }}>
                    <CalendarIcon sx={{ color: "#2af598", fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: "#fff", flex: 1, fontSize: "14px" }}>
                      {months[item.month - 1]} {item.year}
                    </Typography>
                    <Chip
                      label={`${performance >= 0 ? '+' : ''}${performance.toFixed(2)}%`}
                      size="small"
                      sx={{
                        backgroundColor: category.color + "20",
                        color: category.color,
                        border: `1px solid ${category.color}40`,
                        fontWeight: "700",
                        minWidth: "85px",
                        fontSize: "12px",
                        height: "24px"
                      }}
                    />
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Chart Section */}
      <Card sx={{
        background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
        mb: 4
      }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: "600", color: "#fff", mb: 1 }}>
            Monthly Performance Trend
          </Typography>
          <Typography variant="body2" sx={{ color: "#aaa", mb: 3 }}>
            Interactive chart showing monthly returns with cumulative performance trend
          </Typography>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: "400px" }}>
              <Line data={chartData} options={chartOptions} />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Filters Section */}
      <Card sx={{
        background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
        mb: 4
      }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Button
              startIcon={<FilterListIcon />}
              variant="outlined"
              sx={{
                color: "#fff",
                borderColor: "rgba(42, 245, 152, 0.3)",
                "&:hover": {
                  borderColor: "#2af598",
                  backgroundColor: "rgba(42, 245, 152, 0.1)"
                }
              }}
            >
              View by Monthly Performance Records
            </Button>
            <Typography variant="body2" sx={{ color: "#aaa" }}>
              Showing {filteredData.length} of {yearlyGraphData.length} records
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: "#aaa" }}>Filter by Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Filter by Year"
                  onChange={(e) => setSelectedYear(e.target.value)}
                  sx={{
                    color: "#fff",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.3)"
                    }
                  }}
                >
                  <MenuItem value="All Years">All Years</MenuItem>
                  {getYearOptions().map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: "#aaa" }}>Filter by Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Filter by Month"
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  sx={{
                    color: "#fff",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.3)"
                    }
                  }}
                >
                  <MenuItem value="All Months">All Months</MenuItem>
                  {months.map((month, index) => (
                    <MenuItem key={index} value={month}>{month}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: "#aaa" }}>Filter by Performance Rating</InputLabel>
                <Select
                  value={selectedRating}
                  label="Filter by Performance Rating"
                  onChange={(e) => setSelectedRating(e.target.value)}
                  sx={{
                    color: "#fff",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.3)"
                    }
                  }}
                >
                  <MenuItem value="All Ratings">All Ratings</MenuItem>
                  <MenuItem value="Exceptional">Exceptional (&gt;5%)</MenuItem>
                  <MenuItem value="Excellent">Excellent (4-5%)</MenuItem>
                  <MenuItem value="Good">Good (2-4%)</MenuItem>
                  <MenuItem value="Moderate">Moderate (0-2%)</MenuItem>
                  <MenuItem value="Negative">Negative (&lt;0%)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: "#aaa" }}>Filter by Status</InputLabel>
                <Select
                  defaultValue="All Status"
                  label="Filter by Status"
                  sx={{
                    color: "#fff",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.3)"
                    }
                  }}
                >
                  <MenuItem value="All Status">All Status</MenuItem>
                  <MenuItem value="Closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Monthly Performance Records Table */}
      <Card sx={{
        background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)"
      }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: "600", color: "#fff", mb: 1 }}>
            Monthly Performance Records
          </Typography>
          <Typography variant="body2" sx={{ color: "#aaa", mb: 3 }}>
            Detailed monthly performance data with editing capabilities
          </Typography>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{
              backgroundColor: "transparent",
              "& .MuiTable-root": {
                backgroundColor: "transparent"
              }
            }}>
              <Table>
                <TableHead sx={{
                  background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                  fontFamily: "Outfit",
                  borderRadius: '10px',
                  textAlign: "center",
                  position: 'sticky',
                  top: 0,
                  zIndex: 1 
                }}>
                  <TableRow>
                    <TableCell sx={{ color: "#fff", fontWeight: "600" }}>Month</TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "600" }}>Year</TableCell>
                    <TableCell align="right" sx={{ color: "#fff", fontWeight: "600" }}>Monthly Performance (%)</TableCell>
                    <TableCell align="right" sx={{ color: "#fff", fontWeight: "600" }}>Cumulative Return (%)</TableCell>
                    <TableCell align="right" sx={{ color: "#fff", fontWeight: "600" }}>Performance Rating</TableCell>
                    <TableCell align="right" sx={{ color: "#fff", fontWeight: "600" }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.slice().reverse().map((item, index) => {
                    const originalIndex = filteredData.length - 1 - index;
                    // Use ONLY amount field (same as Flutter app)
                    const performance = item.amount || 0;
                    const category = getPerformanceCategory(performance);
                    const cumulative = getCumulativeReturn(originalIndex);
                    
                    return (
                      <TableRow key={index} sx={{
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.05)"
                        }
                      }}>
                        <TableCell sx={{ color: "#fff" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CalendarIcon sx={{ color: "#2af598", fontSize: 20 }} />
                            {months[item.month - 1]}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>{item.year}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                            <TrendingUpIcon sx={{ color: performance >= 0 ? "#2af598" : "#f44336", fontSize: 18 }} />
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                color: performance > 5 ? "#ff9800" : (performance >= 4 ? "#ffc107" : (performance >= 0 ? "#2af598" : "#f44336")),
                                fontWeight: "600"
                              }}
                            >
                              {performance >= 0 ? '+' : ''}{performance.toFixed(2)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" sx={{ color: "#fff", fontWeight: "600" }}>
                            {cumulative.toFixed(2)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={category.label}
                            size="small"
                            sx={{
                              backgroundColor: category.color + "20",
                              color: category.color,
                              border: `1px solid ${category.color}40`,
                              fontWeight: "600"
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                            <CheckCircleIcon sx={{ color: "#2af598", fontSize: 18 }} />
                            <Typography variant="body1" sx={{ color: "#2af598" }}>Closed</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default FundAnalytics;
